import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { PickerDate } from "../../../analyze/context/DateProvider";
import { IScheduleMarqueeFormDataNew } from "../../types";
import { INITIAL_MARQUEE_SETTINGS } from "../configs";
import { useLanguage } from "../context";
import { scheduleMarqueeGroup } from "../types/interfaces";
import { validateMarqueeGroupDateRange } from "../utils";
import { mergeLocaleDataForNewFormat } from "../utils/convertMarqueeData";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import { normalizeMarqueeData } from "../utils/normalizeMarqueeData";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";

interface UseMarqueeSettingsProps {
    characterId: string;
}

const MARQUEE_LIMITS = 5;
const MARQUEE_MIN_DATE = dayjs().subtract(0, "day");
const MARQUEE_MAX_DATE = dayjs().add(100, "year");

export const useMarqueeSettings = ({ characterId }: UseMarqueeSettingsProps) => {
    const { t } = useTranslation();
    const { languageOptions } = useLanguage();
    const [scheduleMarqueeGroups, setScheduleMarqueeGroups] = useState<Array<scheduleMarqueeGroup>>([]);
    const { kioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({ characterId });
    const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
    const [isLoadingSettings, setIsLoadingSettings] = useState<boolean>(true);

    const methods = useForm<IScheduleMarqueeFormDataNew>({
        defaultValues: {
            schedule_marquee: {
                enable_marquee: INITIAL_MARQUEE_SETTINGS.enable_marquee,
                position: INITIAL_MARQUEE_SETTINGS.position,
                data:
                    INITIAL_MARQUEE_SETTINGS.data.length > 0
                        ? INITIAL_MARQUEE_SETTINGS.data
                        : [
                              {
                                  color_bg: "#333333f0",
                                  color_text: "#ffffffff",
                                  start_at: dayjs().format("YYYY/MM/DD HH:mm"),
                                  end_at: dayjs().add(1, "day").format("YYYY/MM/DD HH:mm"),
                                  content: [{}],
                                  selectedLanguages: [languageOptions[0]?.value || "zh-TW"],
                              },
                          ],
            },
        },
        mode: "onChange",
    });

    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: "schedule_marquee.data",
    });

    const canAddMoreMarquee = fields.length < MARQUEE_LIMITS;
    const enabledWatch = useWatch({ control: methods.control, name: "schedule_marquee.enable_marquee" });

    // ======================= Toggle Handler =======================
    const isEnabled = enabledWatch;
    const handleToggle = useCallback(
        (enabled: boolean) => {
            methods.setValue("schedule_marquee.enable_marquee", enabled, { shouldDirty: true });
        },
        [methods]
    );

    const revalidateAllGroups = useCallback((): boolean => {
        const currentData = methods.getValues();
        const allGroups = currentData.schedule_marquee.data;

        const nextValidationErrors = allGroups.map((_, idx) =>
            validateMarqueeGroupDateRange({
                t,
                groupIndex: idx,
                groupData: allGroups[idx],
                allGroupsData: allGroups,
                MIN_DATE: MARQUEE_MIN_DATE,
            })
        );

        setScheduleMarqueeGroups((prev) =>
            prev.map((group, idx) => ({ ...group, validationErrors: nextValidationErrors[idx] }))
        );

        return nextValidationErrors.some((errors) => errors.length > 0);
    }, [methods, t]);

    const handleSubmit = useCallback(
        async (data: IScheduleMarqueeFormDataNew) => {
            try {
                // 跑馬燈關閉時內容不會生效，直接放行儲存，避免被殘留的日期／文字錯誤卡住。
                const hasErrors = data.schedule_marquee.enable_marquee ? revalidateAllGroups() : false;

                if (hasErrors) {
                    customToast.error(t("APPWIDGET.MARQUEE_VALIDATION_FAILED") || "驗證失敗，請修正錯誤");
                    return;
                }

                const apiPayload = {
                    schedule_marquee: {
                        enable_marquee: data.schedule_marquee.enable_marquee,
                        position: data.schedule_marquee.position,
                        data: data.schedule_marquee.data.map((item) => {
                            const filteredContent = item.content
                                .map((contentItem) => {
                                    const filtered: Record<string, { speed: number; text: string }> = {};
                                    Object.keys(contentItem).forEach((langCode) => {
                                        const langContent = contentItem[langCode];
                                        if (langContent && langContent.text && langContent.text.trim()) {
                                            filtered[langCode] = {
                                                speed: Number(langContent.speed || 50),
                                                text: langContent.text.trim(),
                                            };
                                        }
                                    });
                                    return filtered;
                                })
                                .filter((contentItem) => Object.keys(contentItem).length > 0);

                            return {
                                color_bg: item.color_bg,
                                color_text: item.color_text,
                                start_at: dayjs(item.start_at, "YYYY/MM/DD HH:mm").toISOString(),
                                end_at: dayjs(item.end_at, "YYYY/MM/DD HH:mm").toISOString(),
                                content: filteredContent,
                            };
                        }),
                    },
                };

                // updateSettings will auto-convert schedule_marquee to legacy marquee format
                const currentNative = kioskSetting?.data?.native;
                const mergedNative = mergeKioskSettings(currentNative, {
                    schedule_marquee: apiPayload.schedule_marquee,
                });

                await updateSettings({ native: mergedNative });

                // 把 defaultValues 推進到剛儲存的資料，isDirty 才會歸零；
                // 否則存檔後表單仍是髒的，切換分頁會誤跳「未儲存變更」提示。
                methods.reset(data, { keepDirty: false, keepErrors: false });

                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
                throw error;
            }
        },
        [updateSettings, kioskSetting?.data?.native, t, revalidateAllGroups, methods]
    );

    const saveHandler = useCallback(async () => {
        await methods.handleSubmit(handleSubmit)();
    }, [methods, handleSubmit]);

    useTabGuard("marquee", methods.formState.isDirty, saveHandler);

    const handleCancel = () => {
        methods.reset();
    };

    const handleAddMarqueeGroup = () => {
        if (fields.length >= MARQUEE_LIMITS) return;

        const newId = scheduleMarqueeGroups.length > 0 ? Math.max(...scheduleMarqueeGroups.map((g) => g.id)) + 1 : 1;

        append({
            color_bg: "#333333f0",
            color_text: "#ffffffff",
            start_at: dayjs().format("YYYY/MM/DD HH:mm"),
            end_at: dayjs().add(1, "day").format("YYYY/MM/DD HH:mm"),
            content: [{}],
            selectedLanguages: [languageOptions[0]?.value || "zh-TW"],
        });

        const newGroup = {
            id: newId,
            title: t("APPWIDGET.MARQUEE_GROUP", { index: newId }) || `跑馬燈組 ${newId}`,
            dateRange: t("APPWIDGET.SELECT_DATE_RANGE") || "請選擇日期範圍",
            startDate: dayjs(),
            endDate: dayjs().add(1, "day"),
        };
        setScheduleMarqueeGroups([...scheduleMarqueeGroups, newGroup]);
        setExpandedIndex(scheduleMarqueeGroups.length);
    };

    const handleRemoveMarqueeGroup = (groupId: number) => {
        const groupIndex = scheduleMarqueeGroups.findIndex((group) => group.id === groupId);
        remove(groupIndex);
        setScheduleMarqueeGroups(scheduleMarqueeGroups.filter((group) => group.id !== groupId));
    };

    const createUIGroups = useCallback(
        (formData: IScheduleMarqueeFormDataNew): scheduleMarqueeGroup[] => {
            return formData.schedule_marquee.data.map((item, index) => ({
                id: index + 1,
                title: t("APPWIDGET.MARQUEE_GROUP", { index: index + 1 }) || `跑馬燈組 ${index + 1}`,
                dateRange: `${item.start_at} ~ ${item.end_at}`,
                startDate: dayjs(item.start_at),
                endDate: dayjs(item.end_at),
            }));
        },
        [t]
    );

    const hasAnyValidationErrors = useCallback(() => {
        return scheduleMarqueeGroups.some((group) => group.validationErrors && group.validationErrors.length > 0);
    }, [scheduleMarqueeGroups]);

    const handleDateRangeChange = (groupIndex: number, field: "start" | "end", date: PickerDate) => {
        if (!date) return;

        const fieldName = field === "start" ? "start_at" : "end_at";
        const formattedDate = date.format("YYYY/MM/DD HH:mm");

        methods.setValue(`schedule_marquee.data.${groupIndex}.${fieldName}`, formattedDate, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });

        const updatedGroups = [...scheduleMarqueeGroups];
        if (updatedGroups[groupIndex]) {
            if (field === "start") {
                updatedGroups[groupIndex].startDate = date;
            } else {
                updatedGroups[groupIndex].endDate = date;
            }
            updatedGroups[groupIndex].dateRange = `${updatedGroups[groupIndex].startDate.format(
                "YYYY/MM/DD HH:mm"
            )} ~ ${updatedGroups[groupIndex].endDate.format("YYYY/MM/DD HH:mm")}`;
        }
        setScheduleMarqueeGroups(updatedGroups);

        revalidateAllGroups();
    };

    useEffect(() => {
        const normalizedData = normalizeMarqueeData(kioskSetting?.data, languageOptions);

        if (normalizedData) {
            methods.reset(normalizedData);
            setScheduleMarqueeGroups(createUIGroups(normalizedData));
        }

        setIsLoadingSettings(false);
    }, [kioskSetting, languageOptions, methods, createUIGroups]);

    useEffect(() => {
        if (!languageOptions || languageOptions.length === 0) return;

        const current = methods.getValues();
        if (!current?.schedule_marquee?.data?.length) return;

        current.schedule_marquee.data.forEach((item, index) => {
            const merged = mergeLocaleDataForNewFormat(item.content, languageOptions);
            methods.setValue(`schedule_marquee.data.${index}.content`, merged, {
                shouldDirty: false,
                shouldTouch: false,
            });
        });
    }, [languageOptions, methods]);

    const syncLanguagesForGroup = useCallback(
        (groupIndex: number, selectedLanguages: string[]) => {
            const validSelected = selectedLanguages.filter((code: string) =>
                languageOptions.some((opt) => opt.value === code)
            );

            if (validSelected.length !== selectedLanguages.length) {
                methods.setValue(`schedule_marquee.data.${groupIndex}.selectedLanguages`, validSelected, {
                    shouldDirty: false,
                    shouldTouch: false,
                });
            }

            const currentContent = methods.getValues(`schedule_marquee.data.${groupIndex}.content.0`) || {};
            const syncedContent: Record<string, { speed: number; text: string }> = {};
            const langsToSync = validSelected.length ? validSelected : selectedLanguages;

            langsToSync.forEach((langCode: string) => {
                const existingSpeed = currentContent[langCode]?.speed;
                syncedContent[langCode] = {
                    speed: existingSpeed !== undefined && existingSpeed !== null ? existingSpeed : 50,
                    text: currentContent[langCode]?.text || "",
                };
            });

            methods.setValue(`schedule_marquee.data.${groupIndex}.content.0`, syncedContent, {
                shouldDirty: true,
                shouldTouch: false,
            });
        },
        [methods, languageOptions]
    );

    useEffect(() => {
        if (!enabledWatch || !languageOptions || languageOptions.length === 0) return;

        const subscription = methods.watch((_, { name }) => {
            const isLanguageChange = name?.includes(".selectedLanguages");
            const isContentChange = name?.includes(".content");

            if (isLanguageChange && name) {
                const match = name.match(/schedule_marquee\.data\.(\d+)\.selectedLanguages/);
                if (match) {
                    const groupIndex = parseInt(match[1], 10);
                    const selectedLanguages = methods.getValues(
                        `schedule_marquee.data.${groupIndex}.selectedLanguages`
                    );

                    if (selectedLanguages && selectedLanguages.length > 0) {
                        syncLanguagesForGroup(groupIndex, selectedLanguages);
                    }
                }
                revalidateAllGroups();
            } else if (isContentChange) {
                revalidateAllGroups();
            }
        });

        return () => subscription.unsubscribe();
    }, [methods, enabledWatch, revalidateAllGroups, languageOptions, syncLanguagesForGroup]);

    return {
        methods,
        handleSubmit,
        handleCancel,
        fields,
        canAddMoreMarquee,
        handleAddMarqueeGroup,
        scheduleMarqueeGroups,
        expandedIndex,
        setExpandedIndex,
        handleRemoveMarqueeGroup,
        handleDateRangeChange,
        isLoadingSettings,
        isUpdatingKioskSetting,
        hasAnyValidationErrors,
        MARQUEE_MIN_DATE,
        MARQUEE_MAX_DATE,
        MARQUEE_LIMITS,
        // Toggle state
        isEnabled,
        handleToggle,
    };
};
